import React, { useState } from 'react';
import { Helmet } from "react-helmet";
import { Link, useHistory } from 'react-router-dom';

import AuthShell from './_AuthShell';
import { useSessionStore } from "../../stores/sessionStore";
import { backendAuthApi } from "../../helpers/dck_helper";
import { Checkbox } from "../../components/DCKeeper";

// 帳號回填與 session 分開存:logout 會清 authUser/authToken,但不能把「記住我」的帳號一起清掉。
const REMEMBERED_LOGIN_KEY = "dckRememberedLogin";

function readRememberedLogin() {
    try {
        const raw = localStorage.getItem(REMEMBERED_LOGIN_KEY);
        if (!raw) return { remember: false, account: "" };
        const parsed = JSON.parse(raw);
        return {
            remember: true,
            account: typeof parsed.account === "string" ? parsed.account : "",
        };
    } catch (e) {
        return { remember: false, account: "" };
    }
}

function writeRememberedLogin(remember, account) {
    try {
        if (!remember) localStorage.removeItem(REMEMBERED_LOGIN_KEY);
        else localStorage.setItem(REMEMBERED_LOGIN_KEY, JSON.stringify({ account: account || "" }));
    } catch (e) {
        /* ignore(SSR/隱私模式) */
    }
}

/**
 * 登入畫面(/auth-login)。版面對齊 Figma DCK-00_共用-登入(260804 版 2128:21427)。
 *
 * 表單元素一律用模板的 form-floating-custom;設計稿把社群登入與註冊區蓋掉,故不放。
 * 登入走真後端契約(ticket 01/02):POST /api-backend/Authenticate/GetToken 取
 * token+customer_id → 身分組裝(DcCustomer/GetById → DcCustomerRole/GetById)補齊
 * 姓名/角色/權限/首登改密標記。mock 對種子使用者不驗密碼(它們本來就沒有密碼),
 * 但管理者新增的帳號一定要用初始密碼才進得來;首登未改密進站後強制變更(ticket 34)。
 */

// 攔截器對 401 的制式文案是「session 失效」情境;登入頁應呈現帳密錯誤(同 Figma 流程文案)
const SESSION_EXPIRED_MSG = "登入已失效,請重新登入";
const BAD_CREDENTIALS_MSG = "帳號或密碼錯誤，請重新輸入。";

const Login = () => {
    const history = useHistory();
    const login = useSessionStore((s) => s.login);
    const remembered = readRememberedLogin();
    const [account, setAccount] = useState(remembered.account);
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [remember, setRemember] = useState(remembered.remember);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const persistRemember = (nextRemember, nextAccount) => {
        writeRememberedLogin(nextRemember, nextAccount);
    };

    const handleAccountChange = (e) => {
        const next = e.target.value;
        setAccount(next);
        if (remember) persistRemember(true, next.trim());
    };

    const handleRememberChange = (on) => {
        setRemember(on);
        persistRemember(on, account.trim());
    };

    const doLogin = async (e) => {
        e.preventDefault();
        setError("");
        if (!account.trim() || !password.trim()) {
            setError("請輸入帳號與密碼。");
            return;
        }
        setBusy(true);
        try {
            const res = await backendAuthApi.getToken({ username: account.trim(), password });
            persistRemember(remember, account.trim());
            login(
                { id: res.customer_id, guid: res.customer_guid, name: res.username, username: res.username },
                res.token,
                { remember } // 勾=localStorage 常駐;不勾=sessionStorage,關瀏覽器即登出
            );
            // 身分組裝:姓名/角色/modules/fields/首登改密標記;失敗不阻擋登入(內部標 fallback)
            await useSessionStore.getState().assembleIdentity();
            history.push("/dashboard");
        } catch (err) {
            // 攔截器 reject 的是已轉譯的字串;GetToken 的 401 = 帳密錯誤,不是 session 失效
            const msg = typeof err === "string" ? err : "登入失敗，請稍後再試。";
            setError(msg === SESSION_EXPIRED_MSG ? BAD_CREDENTIALS_MSG : msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>登入 | DC Keeper</title>
            </Helmet>
            <AuthShell title="歡迎使用 DC Keeper" subtitle="數位資產管理系統，請登入您的帳號。">
                <form className="mt-4 pt-2" onSubmit={doLogin}>
                    <div className="form-floating form-floating-custom mb-4">
                        <input type="text" className="form-control" id="input-account"
                            placeholder="example@mail.com" value={account}
                            autoComplete="username"
                            onChange={handleAccountChange} />
                        <label htmlFor="input-account">帳號</label>
                        <div className="form-floating-icon">
                            <i className="bx bx-user" />
                        </div>
                    </div>

                    <div className="form-floating form-floating-custom mb-4 auth-pass-inputgroup">
                        <input type={showPw ? "text" : "password"} className="form-control pe-5"
                            id="password-input" placeholder="請輸入密碼" value={password}
                            onChange={(e) => setPassword(e.target.value)} />
                        <button type="button" className="btn btn-link position-absolute h-100 end-0 top-0"
                            aria-label={showPw ? "隱藏密碼" : "顯示密碼"} onClick={() => setShowPw(!showPw)}>
                            <i className={`mdi ${showPw ? "mdi-eye-off-outline" : "mdi-eye-outline"} font-size-18 text-muted`} />
                        </button>
                        <label htmlFor="password-input">密碼</label>
                        <div className="form-floating-icon">
                            <i className="bx bx-lock" />
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col">
                            {/* 「記住我」:勾=localStorage 常駐 + 下次登入回填帳號;
                                不勾=sessionStorage(關瀏覽器即登出)且不留帳號。
                                勾選框走共用的 <Checkbox>(Figma `checkbox_icons`),不散寫 form-check-input */}
                            <Checkbox
                                id="remember-check"
                                className="font-size-13"
                                checked={remember}
                                onChange={handleRememberChange}
                                label={<span className="font-size-13">記住我</span>}
                            />
                        </div>
                        <div className="col-auto">
                            <Link to="/auth-recoverpw" className="text-muted font-size-13">忘記密碼 ?</Link>
                        </div>
                    </div>

                    {error ? <div className="alert alert-danger py-2" role="alert">{error}</div> : null}

                    <div className="mb-3">
                        <button className="btn btn-primary w-100 waves-effect waves-light"
                            type="submit" disabled={busy}>登入</button>
                    </div>
                </form>
            </AuthShell>
        </React.Fragment>
    );
};

export default Login;
